import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '/src/api';
import {
    useAuthContext,
    useCartContext,
    useMessagesContext,
    useModalsContext,
    useStoreConfigContext
} from '/src/providers';
import {
    ButtonAdd,
    Errors,
    Modal,
    ProductVariant,
    ProductVariantsWrapper,
    Thumbnail,
    Price,
    QuantityButtons,
    MaxSaleQtyMessage,
} from '/src/components';
import { STOCK_STATUSES } from '/src/consts';
import {googleSelectItem, googleViewItemList} from '/src/services';
import graphQLQueries from './graph-ql-queries';
import * as  styles from './styles.scss';

const ConfigurableProduct = ({
    sku,
    name: parentName,
    url_key,
    productVariants,
    defaultProductSizeForRecipe,
}) => {
    const {
        storeConfig: { product_url_suffix },
        translate,
        formatPrice,
    } = useStoreConfigContext();
    const {
        cart: { id: cartId },
        isLoadingCart,
        addProductsToCart,
    } = useCartContext();
    const { isAuth } = useAuthContext();
    const { showMessage, MESSAGES_TYPES } = useMessagesContext();
    const { hideModal } = useModalsContext();
    const [selectedVariantSKU, setSelectedVariantSKU] = useState(null);
    const [errors, setErrors] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const {
        data,
        isLoading: isLoadingProductVariants,
        runQuery,
        setValues,
    } = useQuery({
        data: [],
        query: graphQLQueries.getProductVariants,
        variables: { sku },
        queryKey: ['products', 'items'],
    });
    const { variants } = data[0] || {};
    const isLoading = isLoadingProductVariants || isLoadingCart;

    const handleAddProduct = () => addProductsToCart({
        isAuth,
        cartId,
        cartItems: [{
            parent_sku: sku,
            sku: selectedVariantSKU,
            quantity,
        }]
    }).then(() => {
        showMessage(MESSAGES_TYPES.ADDED_PRODUCT_MESSAGE, { addedProducts: [selectedVariantSKU] });
        hideModal();
    }).catch(errors => setErrors(errors));

    const getSelectedVariant = () => variants?.find(({ product: { sku } }) => sku === selectedVariantSKU);

    const getMaxSaleQuantity = () => getSelectedVariant()?.product.stock_data.max_sale_qty;

    const getStockStatus = () => {
        const { product: { stock_status } } = getSelectedVariant() || { product: {} };

        return stock_status
    };

    const isInStock = getStockStatus();

    const {
        product: {
            name,
            image: { url,  label } = {},
            price_range,
            comparison_unit,
            qty_comparison,
        }
    } = getSelectedVariant() || { product: {} };

    useEffect(() => {
        if (variants) {
            const variant = variants.find(({
                product: { default_product_size, stock_status }
            }) => default_product_size && stock_status === STOCK_STATUSES.IN_STOCK);

            if (defaultProductSizeForRecipe) {
                setSelectedVariantSKU(defaultProductSizeForRecipe)
            } else if (variant) {
                const { product: { sku } } = variant;

                setSelectedVariantSKU(sku);
            } else {
                const {
                    product: { sku }
                } = variants.find(({
                    product: { stock_status }
                }) => stock_status === STOCK_STATUSES.IN_STOCK) || { product: {} };

                if (sku) {
                    setSelectedVariantSKU(sku);
                }
            }

            googleViewItemList({ sku, name: parentName, variants });
        }
    }, [data]);

    useEffect(() => {
        if (selectedVariantSKU) {
            const {
                product: {
                    sku: variantSKU,
                    name: variantName,
                    price_range,
                }
            } = getSelectedVariant();

            googleSelectItem({
                sku,
                name,
                variantSKU,
                variantName,
                price_range
            });
        }
    }, [selectedVariantSKU]);

    useEffect(() => {
        if (productVariants) {
            setValues({ data: [{ variants:  productVariants }] });
        } else {
            runQuery({ isMethodPost: isAuth });
        }
    }, []);

    return (
        <Modal { ...(!isLoading ? { toggle: hideModal } : {}) }>
            <Errors errors={errors} />
            <div className={styles.variants}>
                <div className={styles.preview}>
                    <Thumbnail.Product>
                        {url && <img src={`${url}?d=400x360`} alt={label} />}
                    </Thumbnail.Product>
                </div>
                <div className={styles.container}>
                    <div className={styles.details}>
                        <div className={styles.title}>{name}</div>
                        <ProductVariantsWrapper className={styles.mid}>
                            {
                                variants?.sort((a, b) => a.product.weight - b.product.weight)
                                    .map(({ product, product: { sku }, attributes }) => (
                                        <ProductVariant
                                            key={sku}
                                            attributes={attributes}
                                            product={product}
                                            isActive={selectedVariantSKU === sku}
                                            handleClick={() => setSelectedVariantSKU(sku)}
                                        />
                                    ))
                            }
                        </ProductVariantsWrapper>
                        <Price
                            className={styles.price}
                            price={price_range}
                            formatPrice={formatPrice}
                            translate={translate}
                            comparisonUnit={comparison_unit}
                            comparisonQty={qty_comparison}
                        />
                        <div className={styles.by}>
                            <QuantityButtons
                                handleRemove={() => setQuantity(quantity - 1)}
                                handleAdd={() => setQuantity(quantity + 1)}
                                quantity={quantity}
                                isLoading={isLoadingCart || !isInStock || !getMaxSaleQuantity()}
                                maxSaleQuantity={getMaxSaleQuantity()}
                            />
                            <ButtonAdd
                                onClick={handleAddProduct}
                                disabled={!selectedVariantSKU || isLoading}
                            >
                                {translate('Add to basket')}
                            </ButtonAdd>
                            <div className={isInStock ? styles.stock : styles.out}>
                                {isInStock ? translate('In stock') : translate('Temporarily out of stock')}
                            </div>
                        </div>
                        <MaxSaleQtyMessage
                            isMax={quantity === getMaxSaleQuantity()}
                            message={translate('Maximum Qty Allowed in Shopping Cart %1', getMaxSaleQuantity())}
                        />
                    </div>
                    <div className={styles.link} onClick={hideModal}>
                        <Link to={`/${url_key}${product_url_suffix}`}>
                            {translate('View product details')}
                        </Link>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ConfigurableProduct;
